// AGC: EmployeeCard - Reusable card component for employee display
// AGC: Used in both employee detail page and employees list page
// AGC: Supports optional occupation percentage with placeholder

import React from 'react'
import type { TeamMember } from '../../types/permissions'
import { ROLE_INFO } from '../../types/rbac'
import type { FarmRole } from '../../types/rbac'
import { User, Clock, MapPin, CheckSquare, Wrench, Package } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'

interface EmployeeCardProps {
  member: TeamMember
  occupationPercentage?: number  // AGC: Optional - shows "--%" if not provided
  showRole?: boolean  // AGC: Show role badge (default: true, false for list page)
  showVerticalIndicator?: boolean  // AGC: Show vertical bar/pill indicator (default: false, true for detail page)
  onClick?: () => void  // AGC: Optional click handler (for list page)
}

/**
 * AGC: EmployeeCard - Reusable employee card component
 * AGC: Features:
 * AGC: - Large avatar, name, job title, email
 * AGC: - Optional occupation percentage widget (shows "--%" if not provided)
 * AGC: - Optional role badge (can be hidden for list page)
 * AGC: - Optional vertical indicator bar (for detail page)
 * AGC: - Pending invitation badge
 * AGC: - Module access icons
 */
export const EmployeeCard: React.FC<EmployeeCardProps> = ({
  member,
  occupationPercentage,
  showRole = true,
  showVerticalIndicator = false,
  onClick,
}) => {
  const { t } = useTranslation()
  
  // AGC: Map TeamMember role to FarmRole
  // AGC: Use _farmRole from farm-scoped data if available, otherwise map from old role
  const farmRole: FarmRole = (member as any)?._farmRole
    || (member.role === 'admin' || member.role === 'manager'
      ? 'owner'
      : member.role === 'worker'
      ? 'worker'
      : 'manager')

  const roleInfo = ROLE_INFO[farmRole] || ROLE_INFO['worker']
  
  // AGC: Occupation percentage display (with placeholder if not provided)
  // AGC: TODO: Replace placeholder with real backend calculation
  const displayPercentage = occupationPercentage !== undefined 
    ? Math.round(occupationPercentage) 
    : '--'
  
  // AGC: Color coding for occupation (green < 80%, red >= 80%)
  const occupationColor = occupationPercentage !== undefined
    ? (occupationPercentage >= 80 ? 'text-red-500' : 'text-green-500')
    : 'text-gray-400'
  
  const occupationBgColor = occupationPercentage !== undefined
    ? (occupationPercentage >= 80 ? 'bg-red-500' : 'bg-green-500')
    : 'bg-gray-300'
  
  // AGC: Vertical indicator color (green < 80%, red >= 80%)
  const indicatorColor = occupationPercentage !== undefined
    ? (occupationPercentage >= 80 ? 'bg-red-500' : 'bg-green-500')
    : 'bg-gray-300'

  return (
    <div 
      className={`bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden ${onClick ? 'cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.01]' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start gap-4 min-w-0">
        {/* AGC: Large Avatar */}
        <div className="flex-shrink-0">
          {member.avatar ? (
            <img
              src={member.avatar}
              alt={member.displayName}
              className="w-20 h-20 rounded-full object-cover border-3 border-gray-200 dark:border-gray-700"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-navy-500 to-navy-700 flex items-center justify-center border-3 border-gray-200 dark:border-gray-700">
              <User className="w-10 h-10 text-white" />
            </div>
          )}
        </div>

        {/* AGC: Member Info */}
        <div className="flex-1 min-w-0 overflow-hidden">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1 truncate">
            {member.displayName}
          </h2>
          {member.jobTitle && (
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1 truncate">{member.jobTitle}</p>
          )}
          <p className="text-sm text-gray-500 dark:text-gray-500 mb-2 truncate">{member.email}</p>
          
          {/* AGC: Role Badge (optional) */}
          {showRole && (
            <div className="flex items-center gap-2 mb-2">
              <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-white ${roleInfo.color} rounded-lg`}>
                <span>{roleInfo.icon}</span>
                {roleInfo.label}
              </span>
              {member.invitationStatus === 'pending' && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs font-medium rounded-full">
                  <Clock className="w-3 h-3" />
                  {t('team.pendingConfirmation') || 'Pendente'}
                </span>
              )}
            </div>
          )}
          
          {/* AGC: Horizontal Occupation Bar (for list page) */}
          {!showVerticalIndicator && (
            <div className="mt-2">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-xs text-gray-600 dark:text-gray-400">
                  {t('team.occupation') || 'Ocupação'}
                </span>
                <span className={`text-xs font-bold ${occupationColor} flex-shrink-0`}>
                  {displayPercentage}%
                </span>
              </div>
              {/* AGC: Progress bar */}
              <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${occupationBgColor}`}
                  style={{
                    width: occupationPercentage !== undefined 
                      ? `${Math.min(occupationPercentage, 100)}%` 
                      : '0%'
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* AGC: Right Side - Vertical Indicator (for detail page only) */}
        {showVerticalIndicator && (
          <div className="flex-shrink-0 flex items-center gap-3">
            <div className="flex flex-col items-center gap-1">
              <div 
                className={`w-3 h-16 rounded-full ${indicatorColor} transition-colors duration-300`}
                title={occupationPercentage !== undefined 
                  ? `${occupationPercentage}% ${t('team.occupation') || 'ocupação'}` 
                  : t('team.occupationNotAvailable') || 'Ocupação não disponível'}
              />
              {occupationPercentage !== undefined && (
                <span className={`text-xs font-medium ${occupationColor}`}>
                  {Math.round(occupationPercentage)}%
                </span>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* AGC: Module Access Icons (only on detail page) */}
      {showVerticalIndicator && (
        <div className="flex items-center gap-3 pt-3 mt-3 border-t border-gray-100 dark:border-gray-700">
          <div className="flex items-center gap-1.5" title={t('tabs.lots') || 'Lotes'}>
            <MapPin className="w-4 h-4 text-green-600 dark:text-green-400" />
            <span className="text-xs text-gray-600 dark:text-gray-400">Lotes</span>
          </div>
          <div className="flex items-center gap-1.5" title={t('tabs.tasks') || 'Tarefas'}>
            <CheckSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-xs text-gray-600 dark:text-gray-400">Tarefas</span>
          </div>
          {(member.role === 'admin' || member.role === 'manager') && (
            <>
              <div className="flex items-center gap-1.5" title={t('navigation.machinery') || 'Maquinário'}>
                <Wrench className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                <span className="text-xs text-gray-600 dark:text-gray-400">Maquinário</span>
              </div>
              <div className="flex items-center gap-1.5" title={t('navigation.inventory') || 'Inventário'}>
                <Package className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                <span className="text-xs text-gray-600 dark:text-gray-400">Inventário</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default EmployeeCard
